import {isFeatureEnabled} from 'feature-flags';
import {closeCurrentDialog, openDialog} from 'platform/components';

import {ApiError} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {interestRoutes} from '@dms/routes';

import {composePath, isNotNilOrEmpty, useNavigate} from 'shared/web';

import {MandatoryFieldsErrorModal} from '../../components/MandatoryFieldsErrorModal/MandatoryFieldsErrorModal';
import {MandatoryFieldMapValue, mandatoryFieldsMap} from '../../utils/mandatoryFieldsMap';

export interface ValidationError {
  message?: string;
  field?: string;
}

type PathType = '/vehicles/:id/edit' | '/vehicles/:id/edit/:tab';

interface MandatoryFieldsOptions {
  interestId?: string;
  isMiniSale?: boolean;
}

export function useMandatoryFieldsErrorDialog(
  vehicleId?: string,
  options?: MandatoryFieldsOptions
) {
  const navigate = useNavigate();

  const isMandatoryFieldsError = (error: ApiError) =>
    error.status === 400 &&
    error.data?.errors?.some((errorData) => errorData?.message?.includes('Mandatory'));

  const handleRedirect = (field: MandatoryFieldMapValue) => {
    if (!vehicleId && !options?.isMiniSale) {
      return closeCurrentDialog();
    }

    if (options?.isMiniSale && options?.interestId) {
      return navigate(
        composePath(interestRoutes.detailBuying, {
          params: {id: options.interestId},
        })
      );
    }

    const path = composePath(field.href as PathType, {
      params: {id: vehicleId, tab: isNotNilOrEmpty(field.tab) ? field.tab : undefined},
    });

    return navigate(path);
  };

  const handleMandatoryFieldsError = (validationErrors: ValidationError[]) => {
    if (!isFeatureEnabled(featureFlags.CONFIGURABLE_MANDATORY_FIELDS)) {
      return;
    }

    const translatedFields = validationErrors.map((item) => item.message ?? '');

    const extractedFields: string[] = validationErrors
      .map((item) => item.field ?? '')
      .filter(Boolean);

    const mappedFields = extractedFields
      .map((field) => mandatoryFieldsMap[field as keyof typeof mandatoryFieldsMap])
      .filter(Boolean);

    openDialog(
      <MandatoryFieldsErrorModal
        mandatoryFields={mappedFields}
        translatedFields={translatedFields}
        onSubmit={handleRedirect}
      />,
      {
        id: 'mandatory-fields-error-modal',
        title: i18n.t('entity.mandatoryFields.errorModal.title'),
      }
    );
  };

  return {
    isMandatoryFieldsError,
    handleMandatoryFieldsError,
  };
}
